/**
 * 银联聚合码收银台（建行 / 邮政 / 农行 / 浙江商行 / 工行 / 邮惠付 / 湖北农信）—— 与模板 bankAggPay.html 对齐。
 * 立即支付：裸 openauth → payAuthCallback 直出（不再 getAuthCode→/process）。
 */
import { fetchBankAggCashierPage } from '@/api/api'
import {
  resolveOAuthProcessLoading,
  runBuyerOAuthForImmediatePay,
  shouldPreferInAppOAuthLaunch
} from '@/utils/unifiedCashierCore'

const CHANNELS = new Set(['jhuid', 'yz', 'nh', 'zjfs', 'gh', 'yzyhf', 'hbnx'])
const PAGE_LOAD_MAX_ATTEMPTS = 3
const PAGE_LOAD_RETRY_MS = 400

export function normalizeBankAggChannel (raw) {
  const key = String(raw || '').trim().toLowerCase()
  return CHANNELS.has(key) ? key : ''
}

/** 把 axios / ApiResult 拒绝体收成可读文案（勿只读 Error.message，否则常变成「页面加载失败」）。 */
export function formatBankAggPageLoadError (err) {
  if (err == null) {
    return '页面加载失败'
  }
  if (typeof err === 'string' && err.trim()) {
    return err.trim()
  }
  const msg = err.message || err.msg || err.error || ''
  if (typeof msg === 'string' && msg.trim()) {
    return msg.trim()
  }
  if (typeof err.code === 'string' || typeof err.code === 'number') {
    return '页面加载失败(' + String(err.code) + ')'
  }
  return '页面加载失败，请检查网络后重试'
}

function sleep (ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * 拉聚合码收银上下文；短暂网络抖动时自动重试，避免页面停在默认 ¥0.00 且 pageCtx 为空。
 */
export async function loadPageContext (channelKey, orderId, token) {
  const channel = normalizeBankAggChannel(channelKey)
  if (!channel) {
    throw new Error('收银台通道无效')
  }
  const oid = String(orderId || '').trim()
  if (!oid) {
    throw new Error('订单号缺失，请重新下单')
  }
  let lastErr = null
  for (let attempt = 1; attempt <= PAGE_LOAD_MAX_ATTEMPTS; attempt++) {
    try {
      const ctx = await fetchBankAggCashierPage(channel, oid, token)
      if (!ctx) {
        throw new Error('页面加载失败')
      }
      return ctx
    } catch (e) {
      lastErr = e
      if (attempt < PAGE_LOAD_MAX_ATTEMPTS) {
        await sleep(PAGE_LOAD_RETRY_MS * attempt)
      }
    }
  }
  throw new Error(formatBankAggPageLoadError(lastErr))
}

export function formatCountdownMmSs (leftMs) {
  const left = Math.max(0, Math.floor(Number(leftMs) / 1000))
  const m = Math.floor(left / 60)
  const s = left % 60
  return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`
}

/**
 * @param {object} ctx BankAggCashierPageVO
 * @param {{ onProgress?: Function }} [opts]
 */
export function startBankAggBuyerOAuth (ctx, opts) {
  const openUrl = String(ctx && ctx.buyerOAuthOpenUrl || '').trim()
  const oauthCtx = {
    buyerOAuthRequired: ctx.buyerOAuthRequired !== false,
    buyerOAuthProcessUrl: '',
    buyerOAuthInAppLaunchUrl: '',
    buyerOAuthOpenUrl: openUrl,
    buyerOAuthExternalLaunchScheme: ctx.buyerOAuthExternalLaunchScheme || '',
    token: ctx.token || '',
    buyerOAuthStateConsumed: !!ctx.buyerOAuthStateConsumed,
    orderPaying: !!ctx.orderPaying
  }
  if (!openUrl || !shouldPreferInAppOAuthLaunch(oauthCtx)) {
    return Promise.reject(new Error('支付授权未配置，请联系客服'))
  }
  const loading = resolveOAuthProcessLoading(oauthCtx)
  return runBuyerOAuthForImmediatePay(oauthCtx, {
    onProgress: opts && opts.onProgress,
    initialHint: loading.detect && loading.detect.sub
  })
}
